/**
 * LiveTrainingChat — chat with the parent LLM (Gemini or Claude master)
 * to record training entries for a target bot. Each turn where a target bot
 * is selected + save-mode is on gets appended to a per-bot "live-trained"
 * training file (template=kb) that shows up in the main library alongside
 * uploaded files.
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, Send, Loader2, User, Bot, Clock, Cpu, RotateCcw, Save,
  Database, Info, Wand2, MessageSquare, Paperclip, FileSpreadsheet, X,
} from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { getAuthHeaders } from '../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PROVIDER_LOGO = {
  google:    '/brand/ast-logo-gemini.png',
  anthropic: '/brand/ast-logo-claude.png',
};

export default function LiveTrainingChat({
  language, agents, providerFilter, onSaved,
  targetKey: controlledTargetKey,
  onTargetKeyChange,
  attachedFiles = [],
  onOpenUpload,
  onDeleteFile,
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [internalTargetKey, setInternalTargetKey] = useState('');
  const targetKey = controlledTargetKey !== undefined ? controlledTargetKey : internalTargetKey;
  const setTargetKey = (v) => {
    if (onTargetKeyChange) onTargetKeyChange(v);
    else setInternalTargetKey(v);
  };
  const [saveMode, setSaveMode] = useState(true);
  const scrollRef = useRef(null);

  const provider = providerFilter || 'google';
  const providerLabel = provider === 'anthropic' ? 'Claude (Anthropic)' : 'Gemini (Google)';

  // Auto-pick the first agent as default target
  useEffect(() => {
    if (agents.length && !targetKey) {
      setTargetKey(agents[0].agent_key);
    }
  }, [agents, targetKey]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const targetBot = useMemo(() => agents.find((a) => a.agent_key === targetKey), [agents, targetKey]);

  const send = async () => {
    if (!input.trim() || sending) return;
    if (saveMode && !targetKey) {
      toast.error(language === 'ar' ? 'اختر البوت المستهدف أولاً' : 'Pick a target bot first');
      return;
    }
    const userMsg = { role: 'user', message: input.trim() };
    const newHistory = [...messages, userMsg];
    setMessages(newHistory);
    setInput('');
    setSending(true);

    try {
      const res = await axios.post(
        `${API_URL}/api/admin/training-files/live-chat`,
        {
          message: userMsg.message,
          history: messages.map((m) => ({ role: m.role, message: m.message })),
          provider,
          target_agent_key: saveMode ? targetKey : null,
          language,
          save_as_training: saveMode,
        },
        { headers: getAuthHeaders() },
      );
      const d = res.data;
      setMessages([...newHistory, {
        role: 'assistant',
        message: d.reply || '(empty)',
        meta: {
          latency_ms: d.latency_ms,
          model: d.model,
          provider: d.provider,
          fallback_from: d.fallback_from,
          saved_to_training: d.saved_to_training,
        },
      }]);
      if (d.saved_to_training) {
        toast.success(language === 'ar' ? `تم حفظه في تدريب ${targetBot?.name_ar || targetKey}` : `Saved to ${targetBot?.name_en || targetKey} training`);
        onSaved?.();
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setMessages([...newHistory, { role: 'assistant', message: `⚠ ${detail}`, meta: { error: true } }]);
    } finally {
      setSending(false);
    }
  };

  const clear = () => { setMessages([]); toast.success(language === 'ar' ? 'تم مسح المحادثة' : 'Cleared'); };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="space-y-4" data-testid="live-training-chat">
      {/* Info banner */}
      <div className="bg-gradient-to-r from-purple-500/15 via-purple-500/5 to-transparent border border-purple-500/30 rounded-2xl p-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-purple-500/20 border border-purple-500/40 flex items-center justify-center shrink-0">
          <Wand2 className="h-5 w-5 text-purple-300" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            {language === 'ar' ? 'التدريب المباشر (Live Training)' : 'Live Training Chat'}
            <img src={PROVIDER_LOGO[provider]} alt={provider} className="w-4 h-4 object-contain rounded" />
            <span className="text-[10px] font-mono text-purple-300">{providerLabel}</span>
          </h3>
          <p className="text-xs text-white/70 mt-1 leading-relaxed">
            {language === 'ar' ? (
              <>
                تحدّث مع الـ<b className="text-purple-300"> {providerLabel} الرئيسي </b>
                واقول له مثلاً <i>"البوت الفلاني يجب أن يعرف أن سعر تأشيرة العمل 3200 درهم..."</i>.
                عند تفعيل الحفظ، كل رسالة تصبح <b>صف تدريب</b> في ملف <code className="bg-black/40 px-1 text-[10px]">Live-trained ({targetKey || 'target_bot'})</code> يظهر في مكتبتك.
              </>
            ) : (
              <>
                Talk to the <b className="text-purple-300"> master {providerLabel} </b>
                like <i>"The visa bot must know that a work visa costs AED 3200..."</i>.
                When Save mode is ON, every exchange becomes a <b>training row</b> in
                <code className="bg-black/40 px-1 text-[10px]">Live-trained ({targetKey || 'target_bot'})</code>.
              </>
            )}
          </p>
        </div>
      </div>

      {/* Target + save toggle */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 bg-white/[0.02] border border-white/10 rounded-2xl p-3">
        <div className="flex-1">
          <Label className="text-[10px] text-white/50 uppercase tracking-wider">
            {language === 'ar' ? 'البوت المستهدف' : 'Target Bot'}
          </Label>
          <Select value={targetKey || undefined} onValueChange={(v) => setTargetKey(v || '')}>
            <SelectTrigger
              className="mt-1 w-full h-10 bg-black/40 border border-white/10 text-white text-sm"
              data-testid="live-target-select"
            >
              <SelectValue placeholder={language === 'ar' ? '— اختر بوت —' : '— pick a bot —'} />
            </SelectTrigger>
            <SelectContent
              className="bg-[#09090b] border-white/10 text-white"
              data-testid="live-target-options"
            >
              {agents.map((a) => {
                const providerTag = a.provider === 'anthropic'
                  ? 'Claude'
                  : a.provider === 'google'
                    ? 'Gemini'
                    : (a.provider || '');
                return (
                  <SelectItem
                    key={a.id}
                    value={a.agent_key}
                    className="text-white focus:bg-white/10 focus:text-white data-[state=checked]:bg-[#d4af37]/15 cursor-pointer"
                  >
                    <span className="flex items-center gap-2">
                      <span>{language === 'ar' ? a.name_ar : a.name_en}</span>
                      <span className="text-[10px] text-white/40 font-mono">{a.agent_key}</span>
                      {providerTag && (
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${
                          a.provider === 'anthropic'
                            ? 'bg-orange-500/15 border-orange-500/40 text-orange-300'
                            : 'bg-blue-500/15 border-blue-500/40 text-blue-300'
                        }`}>
                          ({providerTag})
                        </span>
                      )}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          {agents.length === 0 && (
            <div className="text-[10px] text-amber-300 mt-1 flex items-center gap-1">
              <Info className="h-3 w-3" />
              {language === 'ar' ? `لا توجد بوتات في ${providerLabel} — أنشئ واحداً من "إدارة الذكاء الاصطناعي"` : `No bots in ${providerLabel} — create one in AI Management`}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 md:ms-3">
          <div>
            <Label className="text-[10px] text-white/50 uppercase tracking-wider">
              {language === 'ar' ? 'حفظ كتدريب' : 'Save as Training'}
            </Label>
            <div className="flex items-center gap-2 mt-1">
              <Switch
                checked={saveMode}
                onCheckedChange={setSaveMode}
                className="data-[state=checked]:bg-emerald-500"
                data-testid="live-save-toggle"
              />
              <span className={`text-xs font-semibold ${saveMode ? 'text-emerald-300' : 'text-white/40'}`}>
                {saveMode ? (language === 'ar' ? 'ON' : 'ON') : 'OFF'}
              </span>
            </div>
          </div>
          <button
            onClick={clear}
            className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 self-end"
            title={language === 'ar' ? 'مسح' : 'Clear'}
            data-testid="live-clear"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Chat area */}
      <div className="bg-white/[0.02] border border-white/10 rounded-2xl h-[440px] flex flex-col">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 ? (
            <div className="text-center py-14">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-purple-500/15 border border-purple-500/30 flex items-center justify-center mb-3">
                <MessageSquare className="h-7 w-7 text-purple-300" />
              </div>
              <h4 className="text-sm font-bold text-white">
                {language === 'ar' ? 'ابدأ محادثة تدريبية' : 'Start a training conversation'}
              </h4>
              <p className="text-xs text-white/50 mt-1 max-w-md mx-auto">
                {language === 'ar'
                  ? 'اكتب معلومة أو تعليمة، وسيقوم النموذج الأم بصياغتها وحفظها كصف تدريب لبوتك المستهدف.'
                  : 'Write a fact or instruction; the master LLM will format & save it as a training row for your target bot.'}
              </p>
            </div>
          ) : (
            messages.map((m, i) => {
              const isBot = m.role === 'assistant';
              return (
                <div key={i} className={`flex gap-2 ${isBot ? '' : 'flex-row-reverse'}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                    isBot ? 'bg-purple-500/20 text-purple-300' : 'bg-emerald-500/20 text-emerald-300'
                  }`}>
                    {isBot ? <Bot className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
                  </div>
                  <div className={`max-w-[80%] ${isBot ? '' : 'text-end'}`}>
                    <div className={`inline-block rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                      isBot
                        ? m.meta?.error
                          ? 'bg-red-500/15 border border-red-500/30 text-red-200 rounded-tl-sm'
                          : 'bg-purple-500/10 border border-purple-500/25 text-white/90 rounded-tl-sm'
                        : 'bg-emerald-500/15 border border-emerald-500/30 text-white rounded-tr-sm'
                    }`}>
                      {m.message}
                    </div>
                    {isBot && m.meta && !m.meta.error && (
                      <div className="flex flex-wrap gap-1 mt-1 text-[9px] text-white/40 font-mono">
                        <span className="inline-flex items-center gap-0.5 bg-black/40 border border-white/10 px-1.5 py-0.5 rounded">
                          <Clock className="h-2.5 w-2.5" />{m.meta.latency_ms} ms
                        </span>
                        <span className="inline-flex items-center gap-0.5 bg-black/40 border border-white/10 px-1.5 py-0.5 rounded">
                          <Cpu className="h-2.5 w-2.5" />{m.meta.model}
                        </span>
                        {m.meta.saved_to_training && (
                          <span className="inline-flex items-center gap-0.5 bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 px-1.5 py-0.5 rounded font-bold">
                            <Save className="h-2.5 w-2.5" />
                            {language === 'ar' ? 'محفوظ كتدريب' : 'Saved to training'}
                          </span>
                        )}
                        {m.meta.fallback_from && (
                          <span className="inline-flex items-center gap-0.5 bg-amber-500/15 border border-amber-500/40 text-amber-300 px-1.5 py-0.5 rounded">
                            fallback:{m.meta.fallback_from}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
          {sending && (
            <div className="flex gap-2">
              <div className="w-7 h-7 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Bot className="h-3.5 w-3.5 text-purple-300" />
              </div>
              <div className="bg-purple-500/10 border border-purple-500/25 rounded-2xl rounded-tl-sm px-4 py-3">
                <Loader2 className="h-4 w-4 animate-spin text-white/60" />
              </div>
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="p-3 border-t border-white/10 space-y-2">
          {/* Attached files pill strip (per selected bot) */}
          {attachedFiles.length > 0 && (
            <div
              className="flex flex-wrap gap-1.5"
              data-testid="live-attached-files"
              dir={language === 'ar' ? 'rtl' : 'ltr'}
            >
              {attachedFiles.map((f) => (
                <span
                  key={f.id}
                  className="inline-flex items-center gap-1.5 max-w-[220px] bg-blue-500/10 border border-blue-500/30 text-blue-200 rounded-full px-2.5 py-1 text-[11px] font-medium"
                  title={f.name}
                  data-testid={`live-attached-file-${f.id}`}
                >
                  <FileSpreadsheet className="h-3 w-3 shrink-0" />
                  <span className="truncate">{f.name}</span>
                  {onDeleteFile && (
                    <button
                      onClick={() => onDeleteFile(f)}
                      className="p-0.5 rounded-full hover:bg-red-500/30 hover:text-red-200 text-blue-300/70 transition"
                      title={language === 'ar' ? 'حذف' : 'Delete'}
                      data-testid={`live-attached-file-remove-${f.id}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </span>
              ))}
            </div>
          )}

          {/* Input row with paperclip embedded inside */}
          <div className="flex gap-2">
            <div className="flex-1 relative">
              {onOpenUpload && (
                <button
                  type="button"
                  onClick={onOpenUpload}
                  disabled={sending}
                  className={`absolute top-1/2 -translate-y-1/2 ${language === 'ar' ? 'right-2' : 'left-2'} p-1.5 rounded-md text-white/60 hover:text-purple-300 hover:bg-white/5 border border-transparent hover:border-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition`}
                  title={language === 'ar' ? 'إرفاق ملف تدريب' : 'Attach training file'}
                  data-testid="live-attach-btn"
                >
                  <Paperclip className="h-4 w-4" />
                </button>
              )}
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={language === 'ar' ? 'اكتب معلومة أو تعليمة تدريب...' : 'Write a training fact or instruction...'}
                disabled={sending}
                className={`w-full bg-black/40 border-white/10 text-white ${onOpenUpload ? (language === 'ar' ? 'pr-10' : 'pl-10') : ''}`}
                dir={language === 'ar' ? 'rtl' : 'ltr'}
                data-testid="live-input"
              />
            </div>
            <Button
              onClick={send}
              disabled={sending || !input.trim() || (saveMode && !targetKey)}
              className="bg-gradient-to-r from-purple-500 to-purple-700 hover:from-purple-500/90 text-white font-bold px-4"
              data-testid="live-send"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
